/**
 * CoolBeansBox Charm Builder - Vanilla JS
 * Multi-step interactive charm bundle configurator
 * Uses Shopify Storefront API for real product data
 */
(function() {
  'use strict';

  // ── Configuration ──
  const CONFIG = {
    storefrontToken: window.__CHARM_BUILDER_TOKEN__ || '',
    shopDomain: window.__CHARM_BUILDER_DOMAIN__ || window.location.hostname,
    maxCharms: 5,
    minCharms: 1,
    bundleDiscount: 0.10, // 10% bundle discount
    categories: [
      { id: 'charm-base', label: 'Bases', icon: '🔗' },
      { id: 'charm-handmade', label: 'Handmade', icon: '🎨' },
      { id: 'charm-toy', label: 'Toys', icon: '🧸' },
      { id: 'charm-figurine', label: 'Figurines', icon: '🗿' },
      { id: 'charm-kawaii', label: 'Kawaii', icon: '🌸' },
      { id: 'charm-retro', label: 'Retro', icon: '🕹️' }
    ],
    rarityTiers: {
      common:    { label: 'Common',    color: '#8B9DAF', badge: '●' },
      rare:      { label: 'Rare',      color: '#5B8DEF', badge: '◆' },
      legendary: { label: 'Legendary', color: '#F5A623', badge: '★' }
    }
  };

  // ── Sample / Fallback Products ──
  const SAMPLE_PRODUCTS = [
    { id: 'sample-1',  title: 'Classic Chain Base',     price: '4.99',  image: null, tags: ['charm-base'],     rarity: 'common' },
    { id: 'sample-2',  title: 'Gold Link Base',         price: '7.99',  image: null, tags: ['charm-base'],     rarity: 'rare' },
    { id: 'sample-3',  title: 'Crystal Base Deluxe',    price: '12.99', image: null, tags: ['charm-base'],     rarity: 'legendary' },
    { id: 'sample-4',  title: 'Painted Heart',          price: '6.99',  image: null, tags: ['charm-handmade'], rarity: 'common' },
    { id: 'sample-5',  title: 'Clay Mushroom',          price: '8.99',  image: null, tags: ['charm-handmade'], rarity: 'rare' },
    { id: 'sample-6',  title: 'Resin Galaxy Orb',       price: '14.99', image: null, tags: ['charm-handmade'], rarity: 'legendary' },
    { id: 'sample-7',  title: 'Mini Dinosaur',          price: '5.99',  image: null, tags: ['charm-toy'],      rarity: 'common' },
    { id: 'sample-8',  title: 'Robot Friend',           price: '9.99',  image: null, tags: ['charm-toy'],      rarity: 'rare' },
    { id: 'sample-9',  title: 'Tiny Chibi Cat',         price: '6.99',  image: null, tags: ['charm-figurine'], rarity: 'common' },
    { id: 'sample-10', title: 'Anime Hero Pose',        price: '11.99', image: null, tags: ['charm-figurine'], rarity: 'rare' },
    { id: 'sample-11', title: 'Sakura Bunny',           price: '7.99',  image: null, tags: ['charm-kawaii'],   rarity: 'common' },
    { id: 'sample-12', title: 'Rainbow Cloud Bear',     price: '10.99', image: null, tags: ['charm-kawaii'],   rarity: 'rare' },
    { id: 'sample-13', title: 'Pixel Sword',            price: '5.99',  image: null, tags: ['charm-retro'],    rarity: 'common' },
    { id: 'sample-14', title: '8-Bit Controller',       price: '8.99',  image: null, tags: ['charm-retro'],    rarity: 'rare' },
    { id: 'sample-15', title: 'Golden Arcade Token',    price: '15.99', image: null, tags: ['charm-retro'],    rarity: 'legendary' }
  ];

  // ── State ──
  const state = {
    step: 1,
    selectedCategory: 'charm-base',
    selectedCharms: [],
    products: [],
    isLoading: true,
    usingSampleData: false
  };

  // ── Storefront API ──
  async function fetchProducts() {
    if (!CONFIG.storefrontToken) {
      console.log('Charm Builder: No Storefront API token — using sample products');
      state.products = SAMPLE_PRODUCTS;
      state.usingSampleData = true;
      state.isLoading = false;
      return;
    }

    const allTags = CONFIG.categories.map(c => c.id);
    const queryParts = allTags.map(tag => `tag:${tag}`).join(' OR ');
    const query = `
      query {
        products(first: 100, query: "${queryParts}") {
          edges {
            node {
              id
              title
              handle
              tags
              priceRange { minVariantPrice { amount currencyCode } }
              images(first: 1) { edges { node { url altText } } }
              variants(first: 1) { edges { node { id price { amount } } } }
            }
          }
        }
      }
    `;

    try {
      const resp = await fetch(`https://${CONFIG.shopDomain}/api/2024-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken
        },
        body: JSON.stringify({ query })
      });
      const json = await resp.json();
      const edges = json?.data?.products?.edges || [];

      if (edges.length === 0) {
        console.log('Charm Builder: No tagged products found — using sample products');
        state.products = SAMPLE_PRODUCTS;
        state.usingSampleData = true;
      } else {
        state.products = edges.map(({ node }) => ({
          id: node.variants?.edges?.[0]?.node?.id || node.id,
          title: node.title,
          handle: node.handle,
          price: node.variants?.edges?.[0]?.node?.price?.amount || node.priceRange?.minVariantPrice?.amount || '0.00',
          image: node.images?.edges?.[0]?.node?.url || null,
          tags: node.tags,
          rarity: node.tags.includes('legendary') ? 'legendary' : node.tags.includes('rare') ? 'rare' : 'common'
        }));
        state.usingSampleData = false;
      }
    } catch (err) {
      console.error('Charm Builder: Storefront API error, falling back to samples', err);
      state.products = SAMPLE_PRODUCTS;
      state.usingSampleData = true;
    }

    state.isLoading = false;
  }

  // ── Helpers ──
  function getProductsByCategory(catId) {
    return state.products.filter(p => (p.tags || []).includes(catId));
  }

  function getSubtotal() {
    return state.selectedCharms.reduce((sum, c) => sum + parseFloat(c.price), 0);
  }

  function getTotal() {
    const sub = getSubtotal();
    return state.selectedCharms.length >= 3 ? sub * (1 - CONFIG.bundleDiscount) : sub;
  }

  function formatPrice(n) {
    return '$' + parseFloat(n).toFixed(2);
  }

  function placeholderSvg(title) {
    const hue = Math.abs(title.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 360;
    return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect width="200" height="200" fill="hsl(${hue},45%,85%)"/><text x="100" y="105" text-anchor="middle" font-family="sans-serif" font-size="14" fill="hsl(${hue},30%,35%)">${title.slice(0,12)}</text></svg>`)}`;
  }

  // ── Cart integration ──
  async function addBundleToCart() {
    const items = state.selectedCharms.map((charm, i) => ({
      id: charm.id.replace('gid://shopify/ProductVariant/', ''),
      quantity: 1,
      properties: {
        '_bundle_id': `charm-bundle-${Date.now()}`,
        '_bundle_position': `${i + 1} of ${state.selectedCharms.length}`,
        '_charm_builder': 'true'
      }
    }));

    // For sample data, show a demo message
    if (state.usingSampleData) {
      showToast('Demo mode — tag your products & set prices to enable real checkout!');
      return;
    }

    try {
      for (const item of items) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [item] })
        });
      }
      showToast('Charm bundle added to cart!');
      setTimeout(() => { window.location.href = '/cart'; }, 1200);
    } catch (err) {
      console.error('Cart error:', err);
      showToast('Error adding to cart. Please try again.');
    }
  }

  function showToast(msg) {
    const existing = document.querySelector('.cb-toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.className = 'cb-toast';
    toast.textContent = msg;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('cb-toast--show'));
    setTimeout(() => {
      toast.classList.remove('cb-toast--show');
      setTimeout(() => toast.remove(), 400);
    }, 2800);
  }

  // ── Rendering ──
  function render() {
    const root = document.getElementById('charm-builder-app');
    if (!root) return;

    root.innerHTML = `
      <div class="cb">
        ${renderHeader()}
        <div class="cb__body">
          ${state.isLoading ? renderLoader() : renderStep()}
        </div>
      </div>
    `;
    attachEvents();
  }

  function renderHeader() {
    const steps = [
      { num: 1, label: 'Choose Charms' },
      { num: 2, label: 'Review Bundle' },
      { num: 3, label: 'Add to Cart' }
    ];
    return `
      <div class="cb__header">
        <h1 class="cb__title">✨ Charm Builder</h1>
        ${state.usingSampleData ? '<p class="cb__demo-badge">Demo Mode — Sample Products</p>' : ''}
        <div class="cb__steps">
          ${steps.map(s => `
            <div class="cb__step ${state.step === s.num ? 'cb__step--active' : ''} ${state.step > s.num ? 'cb__step--done' : ''}">
              <span class="cb__step-num">${state.step > s.num ? '✓' : s.num}</span>
              <span class="cb__step-label">${s.label}</span>
            </div>
          `).join('<div class="cb__step-line"></div>')}
        </div>
      </div>
    `;
  }

  function renderLoader() {
    return '<div class="cb__loader"><div class="cb__spinner"></div><p>Loading charms...</p></div>';
  }

  function renderStep() {
    switch (state.step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      default: return '';
    }
  }

  // ── Step 1: Choose Charms ──
  function renderStep1() {
    const products = getProductsByCategory(state.selectedCategory);
    return `
      <div class="cb__step1">
        <div class="cb__categories">
          ${CONFIG.categories.map(cat => `
            <button class="cb__cat-btn ${state.selectedCategory === cat.id ? 'cb__cat-btn--active' : ''}"
                    data-cat="${cat.id}">
              <span class="cb__cat-icon">${cat.icon}</span>
              <span class="cb__cat-label">${cat.label}</span>
            </button>
          `).join('')}
        </div>
        <div class="cb__grid-and-preview">
          <div class="cb__product-grid">
            ${products.length === 0
              ? '<p class="cb__empty">No charms in this category yet.</p>'
              : products.map(p => renderProductCard(p)).join('')}
          </div>
          <div class="cb__preview">
            <h3 class="cb__preview-title">Your Bundle <span class="cb__count">${state.selectedCharms.length}/${CONFIG.maxCharms}</span></h3>
            ${state.selectedCharms.length === 0
              ? '<p class="cb__preview-empty">Pick some charms to get started!</p>'
              : `
                <ul class="cb__preview-list">
                  ${state.selectedCharms.map((c, i) => `
                    <li class="cb__preview-item">
                      <img src="${c.image || placeholderSvg(c.title)}" alt="${c.title}" class="cb__preview-img" />
                      <div class="cb__preview-info">
                        <span class="cb__preview-name">${c.title}</span>
                        <span class="cb__preview-price">${formatPrice(c.price)}</span>
                      </div>
                      <button class="cb__remove-btn" data-remove="${i}" title="Remove">✕</button>
                    </li>
                  `).join('')}
                </ul>
                <div class="cb__preview-totals">
                  <div class="cb__subtotal">Subtotal: ${formatPrice(getSubtotal())}</div>
                  ${state.selectedCharms.length >= 3 ? `<div class="cb__discount">Bundle discount (${CONFIG.bundleDiscount * 100}% off): -${formatPrice(getSubtotal() * CONFIG.bundleDiscount)}</div>` : '<div class="cb__discount-hint">Add ${3 - state.selectedCharms.length} more for 10% off!</div>'}
                  <div class="cb__total">Total: ${formatPrice(getTotal())}</div>
                </div>
              `}
            ${state.selectedCharms.length >= CONFIG.minCharms ? `<button class="cb__next-btn" data-action="next">Review Bundle →</button>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  function renderProductCard(product) {
    const rarity = CONFIG.rarityTiers[product.rarity] || CONFIG.rarityTiers.common;
    const isSelected = state.selectedCharms.some(c => c.id === product.id);
    const isFull = state.selectedCharms.length >= CONFIG.maxCharms;
    return `
      <div class="cb__card ${isSelected ? 'cb__card--selected' : ''} ${isFull && !isSelected ? 'cb__card--disabled' : ''}"
           data-product-id="${product.id}">
        <div class="cb__card-img-wrap">
          <img src="${product.image || placeholderSvg(product.title)}" alt="${product.title}" class="cb__card-img" loading="lazy" />
          <span class="cb__rarity" style="background:${rarity.color}">${rarity.badge} ${rarity.label}</span>
        </div>
        <div class="cb__card-info">
          <h4 class="cb__card-title">${product.title}</h4>
          <span class="cb__card-price">${formatPrice(product.price)}</span>
        </div>
        <button class="cb__add-btn" ${isFull && !isSelected ? 'disabled' : ''}>
          ${isSelected ? '✓ Added' : '+ Add'}
        </button>
      </div>
    `;
  }

  // ── Step 2: Review ──
  function renderStep2() {
    return `
      <div class="cb__step2">
        <h2 class="cb__review-title">Review Your Charm Bundle</h2>
        <div class="cb__review-grid">
          ${state.selectedCharms.map((c, i) => `
            <div class="cb__review-card">
              <img src="${c.image || placeholderSvg(c.title)}" alt="${c.title}" class="cb__review-img" />
              <h4>${c.title}</h4>
              <span class="cb__review-price">${formatPrice(c.price)}</span>
              <button class="cb__remove-btn" data-remove="${i}">Remove</button>
            </div>
          `).join('')}
        </div>
        <div class="cb__review-summary">
          <div>Items: ${state.selectedCharms.length}</div>
          <div>Subtotal: ${formatPrice(getSubtotal())}</div>
          ${state.selectedCharms.length >= 3 ? `<div class="cb__discount">Bundle discount: -${formatPrice(getSubtotal() * CONFIG.bundleDiscount)}</div>` : ''}
          <div class="cb__total-big">Total: ${formatPrice(getTotal())}</div>
        </div>
        <div class="cb__review-actions">
          <button class="cb__back-btn" data-action="back">← Back to Charms</button>
          <button class="cb__next-btn" data-action="next">Add to Cart 🛒</button>
        </div>
      </div>
    `;
  }

  // ── Step 3: Confirmation ──
  function renderStep3() {
    return `
      <div class="cb__step3">
        <div class="cb__confirm">
          <div class="cb__confirm-icon">🎉</div>
          <h2>Bundle Added!</h2>
          <p>Your ${state.selectedCharms.length}-charm bundle has been added to your cart.</p>
          <div class="cb__confirm-actions">
            <a href="/cart" class="cb__next-btn">View Cart →</a>
            <button class="cb__back-btn" data-action="restart">Build Another</button>
          </div>
        </div>
      </div>
    `;
  }

  // ── Events ──
  function attachEvents() {
    const root = document.getElementById('charm-builder-app');
    if (!root) return;

    // Category tabs
    root.querySelectorAll('[data-cat]').forEach(btn => {
      btn.addEventListener('click', () => {
        state.selectedCategory = btn.dataset.cat;
        render();
      });
    });

    // Product cards
    root.querySelectorAll('.cb__card').forEach(card => {
      card.addEventListener('click', () => {
        const pid = card.dataset.productId;
        const idx = state.selectedCharms.findIndex(c => c.id === pid);
        if (idx > -1) {
          state.selectedCharms.splice(idx, 1);
        } else if (state.selectedCharms.length < CONFIG.maxCharms) {
          const product = state.products.find(p => p.id === pid);
          if (product) state.selectedCharms.push({ ...product });
        }
        render();
      });
    });

    // Remove buttons
    root.querySelectorAll('[data-remove]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        state.selectedCharms.splice(parseInt(btn.dataset.remove), 1);
        render();
      });
    });

    // Navigation
    root.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        if (action === 'next') {
          if (state.step === 2) {
            addBundleToCart();
            state.step = 3;
          } else {
            state.step++;
          }
        } else if (action === 'back') {
          state.step = Math.max(1, state.step - 1);
        } else if (action === 'restart') {
          state.step = 1;
          state.selectedCharms = [];
        }
        render();
      });
    });
  }

  // ── Init ──
  async function init() {
    render(); // Show loading state
    await fetchProducts();
    render(); // Show products
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();